import { c as _c } from "react/compiler-runtime";
import { useCustomQuery } from './useCustomQuery';
import { analyticsApi } from '../api/endpoints';
import type { TrendAnalytics } from '../api/analytics-types';

// ============================================================================
// Shared Types
// ============================================================================

/**
 * Object types that support trend analytics
 */
export type TrendObjectType = 'brand' | 'product' | 'collection';

/**
 * Period for trend aggregation
 */
export type TrendPeriod = 'daily' | 'weekly' | 'monthly';

/**
 * Base parameters for trend analytics queries
 */
export interface BaseTrendAnalyticsParams {
  objectType: TrendObjectType;
  objectId: number;
  startDate?: string;
  endDate?: string;
  period?: TrendPeriod;
}

// ============================================================================
// Query Keys
// ============================================================================

/**
 * Shared query key factory for trend analytics
 * Supports multiple object types (brand, product, collection, etc.)
 */
export const trendQueryKeys = {
  trends: (objectType: TrendObjectType, objectId: number, startDate?: string, endDate?: string, period?: TrendPeriod) => [`${objectType}-analytics`, objectId, 'trends', startDate, endDate, period] as const
} as const;

// ============================================================================
// API Client Functions
// ============================================================================

/**
 * Fetch trend analytics for any supported object type
 *
 * This is the core implementation that can be reused for brands, products, collections, etc.
 *
 * @param params - Query parameters including objectType, objectId, and optional date range and period
 * @returns Promise resolving to trend analytics data
 */
async function fetchTrendAnalytics(params: BaseTrendAnalyticsParams): Promise<TrendAnalytics> {
  return analyticsApi.getObjectTrends(params.objectType, params.objectId, {
    startDate: params.startDate,
    endDate: params.endDate,
    period: params.period
  });
}

// ============================================================================
// Base Hook
// ============================================================================

/**
 * Base hook for fetching trend analytics
 *
 * This is the core implementation that can be wrapped for specific object types.
 * Use the specialized hooks (useBrandTrendAnalytics, useProductTrendAnalytics, etc.)
 * instead of calling this directly.
 *
 * @param params - Base analytics query parameters
 * @returns Query result with trend data, loading state, and error state
 */
export function useTrendAnalytics(params) {
  const $ = _c(12);
  let t0;
  if ($[0] !== params.endDate || $[1] !== params.objectId || $[2] !== params.objectType || $[3] !== params.period || $[4] !== params.startDate) {
    t0 = trendQueryKeys.trends(params.objectType, params.objectId, params.startDate, params.endDate, params.period);
    $[0] = params.endDate;
    $[1] = params.objectId;
    $[2] = params.objectType;
    $[3] = params.period;
    $[4] = params.startDate;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  let t1;
  if ($[6] !== params) {
    t1 = () => fetchTrendAnalytics(params);
    $[6] = params;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const t2 = !!params.objectId;
  let t3;
  if ($[8] !== t0 || $[9] !== t1 || $[10] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      staleTime: 10000,
      enabled: t2
    };
    $[8] = t0;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return useCustomQuery(t3);
}

// ============================================================================
// Brand-Specific Hook
// ============================================================================

/**
 * Parameters for brand trend analytics
 */
export interface UseEntityTrendAnalyticsParams {
  entityId: number;
  startDate?: string;
  endDate?: string;
  period?: TrendPeriod;
}

/**
 * Hook to fetch trend analytics for a brand
 *
 * Fetches time-series reaction data with anomaly detection for brand engagement trends.
 * Uses TanStack Query with 5-minute staleTime for automatic caching and refetching.
 *
 * **Validates: Requirements 3.1, 3.2, 9.5**
 *
 * @param params - Analytics query parameters (brandId, optional date range and period)
 * @returns Query result with trend data, loading state, and error state
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useBrandTrendAnalytics({
 *   brandId: 123,
 *   startDate: '2024-01-01',
 *   endDate: '2024-01-31',
 *   period: 'daily'
 * });
 *
 * if (isLoading) return <Spinner />;
 * if (error) return <ErrorMessage error={error} />;
 *
 * return (
 *   <div>
 *     <h2>Trend Period: {data.period}</h2>
 *     <LineChart data={data.trendData} />
 *     {data.anomalies.length > 0 && (
 *       <AnomalyList anomalies={data.anomalies} />
 *     )}
 *   </div>
 * );
 * ```
 */
export function useBrandTrendAnalytics(params) {
  const $ = _c(5);
  let t0;
  if ($[0] !== params.endDate || $[1] !== params.entityId || $[2] !== params.period || $[3] !== params.startDate) {
    t0 = {
      objectType: "brand",
      objectId: params.entityId,
      startDate: params.startDate,
      endDate: params.endDate,
      period: params.period
    };
    $[0] = params.endDate;
    $[1] = params.entityId;
    $[2] = params.period;
    $[3] = params.startDate;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  return useTrendAnalytics(t0);
}

// ============================================================================
// Future Hook Examples (for reference)
// ============================================================================

/**
 * Example: Hook for product trend analytics
 *
 * export interface UseProductTrendAnalyticsParams {
 *   productId: number;
 *   startDate?: string;
 *   endDate?: string;
 *   period?: TrendPeriod;
 * }
 *
 * export function useProductTrendAnalytics(params: UseProductTrendAnalyticsParams) {
 *   return useTrendAnalytics({
 *     objectType: 'product',
 *     objectId: params.productId,
 *     startDate: params.startDate,
 *     endDate: params.endDate,
 *     period: params.period,
 *   });
 * }
 */

/**
 * Example: Hook for collection trend analytics
 *
 * export interface UseCollectionTrendAnalyticsParams {
 *   collectionId: number;
 *   startDate?: string;
 *   endDate?: string;
 *   period?: TrendPeriod;
 * }
 *
 * export function useCollectionTrendAnalytics(params: UseCollectionTrendAnalyticsParams) {
 *   return useTrendAnalytics({
 *     objectType: 'collection',
 *     objectId: params.collectionId,
 *     startDate: params.startDate,
 *     endDate: params.endDate,
 *     period: params.period,
 *   });
 * }
 */